import { FileText, Scale, Shield, AlertTriangle, Globe, MessageCircle, Instagram, Megaphone, Users, CheckCircle, XCircle, Mail, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";

const TermsOfService = () => {
  const lastUpdated = "February 3, 2026";
  const companyName = "Vendas Group";
  const appName = "Huggy Bits Connect";
  const contactEmail = "contato@vendas.group";
  const legalEmail = "legal@vendas.group";
  const websiteUrl = "https://huggy-bits-connect.lovable.app";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4">
          <Button variant="ghost" asChild className="mb-4 text-primary-foreground hover:text-primary-foreground/80">
            <Link to="/auth">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <Scale className="h-10 w-10" />
            <h1 className="text-3xl md:text-4xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-primary-foreground/80 text-lg">
            {appName} by {companyName}
          </p>
          <p className="text-primary-foreground/60 text-sm mt-2">
            Last updated: {lastUpdated}
          </p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Agreement to Terms */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            Agreement to Terms
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            These Terms of Service ("Terms") govern your access to and use of the 
            {appName} platform and services provided by {companyName} ("we", "us", or "our").
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            By accessing or using our services, you agree to be bound by these Terms. 
            If you disagree with any part of these Terms, you may not access or use our services.
          </p>
          <div className="bg-amber-500/10 p-4 rounded-lg border border-amber-500/30">
            <p className="text-sm text-foreground">
              <strong>Important:</strong> By using integrations with Meta platforms 
              (Facebook, Instagram, WhatsApp), you also agree to comply with the 
              Meta Platform Terms and Meta's Community Standards.
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Description of Services */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            Description of Services
          </h2>
          <p className="text-muted-foreground mb-4">
            {appName} is a Customer Relationship Management (CRM) platform that provides:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-primary" />
                  WhatsApp Integration
                </h3>
                <p className="text-sm text-muted-foreground">
                  Send and receive WhatsApp messages, manage conversations, 
                  and automate customer communication.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Instagram className="h-5 w-5 text-primary" />
                  Instagram Direct
                </h3>
                <p className="text-sm text-muted-foreground">
                  Manage Instagram Direct messages from a unified inbox, 
                  respond to customers, and track conversations.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-primary" />
                  Lead Management
                </h3>
                <p className="text-sm text-muted-foreground">
                  Import leads from Meta Lead Ads, manage sales pipelines, 
                  and track customer journeys.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Team Collaboration
                </h3>
                <p className="text-sm text-muted-foreground">
                  Assign leads to team members, manage permissions, 
                  and track team performance.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Meta Platform Integration Terms */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Meta Platform Integration Terms
          </h2>
          
          <div className="bg-primary/5 p-6 rounded-lg border border-primary/20 mb-6">
            <h3 className="font-semibold mb-3">Compliance with Meta Policies</h3>
            <p className="text-muted-foreground text-sm mb-4">
              When using {appName}'s integrations with Meta platforms, you agree to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground text-sm space-y-2">
              <li>Comply with the <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer" className="text-primary underline">Meta Platform Terms</a></li>
              <li>Comply with <a href="https://www.facebook.com/communitystandards/" target="_blank" rel="noopener noreferrer" className="text-primary underline">Meta Community Standards</a></li>
              <li>Comply with <a href="https://www.whatsapp.com/legal/business-terms" target="_blank" rel="noopener noreferrer" className="text-primary underline">WhatsApp Business Terms</a></li>
              <li>Use the services only for legitimate business purposes</li>
              <li>Not violate any applicable laws or regulations</li>
            </ul>
          </div>

          {/* Instagram Direct Terms */}
          <Card className="mb-4">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Instagram className="h-5 w-5 text-primary" />
                Instagram Direct Messaging Terms
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                By using Instagram Direct integration, you agree to:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Permitted Uses
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Respond to customer inquiries</li>
                    <li>• Provide customer support</li>
                    <li>• Share product/service information</li>
                    <li>• Send transactional messages</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                    <XCircle className="h-4 w-4 text-destructive" />
                    Prohibited Uses
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Unsolicited promotional messages</li>
                    <li>• Spam or bulk messaging</li>
                    <li>• Harassment or abuse</li>
                    <li>• Selling user data</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lead Ads Terms */}
          <Card className="mb-4">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-primary" />
                Meta Lead Ads Integration Terms
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                When importing leads from Meta Lead Ads, you acknowledge and agree that:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-2">
                <li>You have obtained proper consent from leads through your ad campaigns</li>
                <li>You will use lead data only for the purposes disclosed in your lead forms</li>
                <li>You are responsible for the content and accuracy of your lead form disclosures</li>
                <li>You will honor opt-out requests from leads promptly</li>
                <li>You will not share lead data with third parties without proper consent</li>
                <li>You will comply with all applicable data protection laws (LGPD, GDPR, CCPA)</li>
              </ul>
            </CardContent>
          </Card>

          {/* WhatsApp Terms */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-primary" />
                WhatsApp Business API Terms
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                When using WhatsApp integration, you agree to:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-2">
                <li>Use only approved message templates for initiating conversations</li>
                <li>Respond within 24 hours of customer messages (session window)</li>
                <li>Not send spam, promotional, or marketing messages without consent</li>
                <li>Maintain proper opt-in records for all contacts</li>
                <li>Follow WhatsApp Business Messaging Policy</li>
              </ul>
            </CardContent>
          </Card>
        </section>

        <Separator className="my-8" />

        {/* User Responsibilities */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            User Responsibilities
          </h2>
          
          <div className="space-y-4">
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">Account Security</h3>
              <p className="text-muted-foreground text-sm">
                You are responsible for maintaining the confidentiality of your account 
                credentials and for all activities under your account. Notify us immediately 
                of any unauthorized access.
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">Accurate Information</h3>
              <p className="text-muted-foreground text-sm">
                You agree to provide accurate, current, and complete information during 
                registration and to update such information to keep it accurate.
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">Legal Compliance</h3>
              <p className="text-muted-foreground text-sm">
                You are responsible for ensuring your use of our services complies with 
                all applicable laws and regulations, including data protection laws.
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">Content Responsibility</h3>
              <p className="text-muted-foreground text-sm">
                You are solely responsible for all content transmitted through our platform, 
                including messages, files, and media shared with your customers.
              </p>
            </div>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Prohibited Activities */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            Prohibited Activities
          </h2>
          
          <p className="text-muted-foreground mb-4">
            You agree NOT to use our services to:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Send spam or unsolicited messages</li>
              <li>Harass, abuse, or harm others</li>
              <li>Impersonate any person or entity</li>
              <li>Violate intellectual property rights</li>
              <li>Transmit malware or malicious code</li>
              <li>Interfere with service operation</li>
            </ul>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Collect user data without consent</li>
              <li>Sell or trade personal information</li>
              <li>Violate Meta Platform policies</li>
              <li>Engage in fraudulent activities</li>
              <li>Circumvent security measures</li>
              <li>Use automated systems without approval</li>
            </ul>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Intellectual Property */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Intellectual Property</h2>
          <p className="text-muted-foreground mb-4">
            The {appName} platform, including its design, features, and content, 
            is owned by {companyName} and protected by intellectual property laws.
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2">
            <li>You retain ownership of your content and data</li>
            <li>You grant us a license to use your content to provide services</li>
            <li>You may not copy, modify, or distribute our platform</li>
            <li>Trademarks of Meta, Instagram, WhatsApp belong to their respective owners</li>
          </ul>
        </section>

        <Separator className="my-8" />

        {/* Service Availability */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Service Availability & Modifications</h2>
          <p className="text-muted-foreground mb-4">
            We strive to maintain continuous service availability, but:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2">
            <li>Services may be interrupted for maintenance or updates</li>
            <li>Third-party integrations depend on external provider availability</li>
            <li>We reserve the right to modify or discontinue features</li>
            <li>We will provide reasonable notice for significant changes</li>
          </ul>
        </section>

        <Separator className="my-8" />

        {/* Limitation of Liability */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Limitation of Liability</h2>
          <div className="bg-muted/50 p-6 rounded-lg">
            <p className="text-muted-foreground text-sm mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc list-inside text-muted-foreground text-sm space-y-2">
              <li>Our services are provided "AS IS" without warranties</li>
              <li>We are not liable for indirect, incidental, or consequential damages</li>
              <li>Our total liability is limited to fees paid in the past 12 months</li>
              <li>We are not responsible for third-party service failures</li>
              <li>We are not liable for actions taken by Meta or other platforms</li>
            </ul>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Indemnification */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Indemnification</h2>
          <p className="text-muted-foreground">
            You agree to indemnify and hold {companyName} harmless from any claims, 
            damages, or expenses arising from your use of our services, violation of 
            these Terms, or violation of any third-party rights, including Meta Platform Terms.
          </p>
        </section>

        <Separator className="my-8" />

        {/* Termination */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Termination</h2>
          <p className="text-muted-foreground mb-4">
            We may terminate or suspend your access immediately, without notice, if:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2">
            <li>You breach these Terms</li>
            <li>You violate Meta Platform policies</li>
            <li>We are required to do so by law</li>
            <li>We discontinue the service</li>
          </ul>
          <p className="text-muted-foreground mt-4">
            Upon termination, your right to use the services will cease immediately. 
            You may request your data for up to 30 days after termination.
          </p>
        </section>

        <Separator className="my-8" />

        {/* Governing Law */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Governing Law</h2>
          <p className="text-muted-foreground">
            These Terms shall be governed by and construed in accordance with the laws 
            of Brazil. Any disputes shall be resolved in the courts of São Paulo, Brazil, 
            without regard to conflict of law principles.
          </p>
        </section>

        <Separator className="my-8" />

        {/* Changes to Terms */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Changes to Terms</h2>
          <p className="text-muted-foreground">
            We reserve the right to modify these Terms at any time. We will notify users 
            of material changes via email or through the platform. Continued use after 
            changes constitutes acceptance of the new Terms.
          </p>
        </section>

        <Separator className="my-8" />

        {/* Contact */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            Contact Us
          </h2>
          <p className="text-muted-foreground mb-4">
            If you have questions about these Terms, please contact us:
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <span className="text-muted-foreground">General inquiries:</span>
              <a href={`mailto:${contactEmail}`} className="text-primary underline">
                {contactEmail}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-muted-foreground" />
              <span className="text-muted-foreground">Legal matters:</span>
              <a href={`mailto:${legalEmail}`} className="text-primary underline">
                {legalEmail}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <a href={websiteUrl} className="text-primary underline" target="_blank" rel="noopener noreferrer">
                {websiteUrl}
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-muted-foreground pt-8 border-t">
          <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
          <p className="mt-2">
            <a href="/politicasdeprivacidade" className="text-primary underline">Privacy Policy</a>
            {" · "}
            <a href="/data-deletion-status" className="text-primary underline">Data Deletion Status</a>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default TermsOfService;
